import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';

export interface CategoryItem {
  uuid: string;
  name: string;
  imageUrl: string | null;
  hasDeals: boolean;
  noOfOrganisations: number;
  noOfProjects: number;
  noOfPortfolios: number;
}

export interface CategoriesResponse {
  response: CategoryItem[];
}

export interface CategoryCheckNameResponse {
  /** true = name already taken; false = name is available */
  response: boolean;
}

export interface CategoryCreateUpdatePayload {
  uuid: string | null;
  name: string;
}

export interface CategoryCreateUpdateResponse {
  response: {
    uuid: string;
    submitted: boolean;
  };
}

export interface CategoryDetailsResponse {
  response: {
    uuid: string;
    name: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  private readonly base = '/v1/general/settings/categories';

  constructor(private apiService: RestfulApiService) {}

  /** Fetch all categories, optionally filtered server-side by search keyword. */
  getCategories(searchKeyword = ''): Observable<CategoriesResponse> {
    const params = searchKeyword ? { searchInput: searchKeyword } : {};
    return this.apiService.get(this.base, params) as Observable<CategoriesResponse>;
  }

  /** Returns true if the name is already taken.
   *  Pass uuid when updating an existing category so the check excludes itself. */
  checkName(name: string, uuid?: string): Observable<CategoryCheckNameResponse> {
    const payload = uuid ? { name, uuid } : { name };
    return this.apiService.post(`${this.base}/check-name`, payload) as Observable<CategoryCheckNameResponse>;
  }

  /** Create or update a category. Pass uuid: null to create. */
  createUpdate(payload: CategoryCreateUpdatePayload): Observable<CategoryCreateUpdateResponse> {
    return this.apiService.post(`${this.base}/create-update`, payload) as Observable<CategoryCreateUpdateResponse>;
  }

  /** Fetch full category details by UUID. */
  getDetails(uuid: string): Observable<CategoryDetailsResponse> {
    return this.apiService.get(`${this.base}/details/${uuid}`) as Observable<CategoryDetailsResponse>;
  }

  /** Delete a category by UUID. */
  delete(uuid: string): Observable<unknown> {
    return this.apiService.delete(`${this.base}/delete/${uuid}`);
  }
}
